// Pure layout math for the Timeline → PDF slide-deck export.
//
// The deck is a "picture book" of an org's platform map across a handful of
// curated pop-scientist highlight snapshots (one page each). The hard rule is
// visual continuity: BF columns and derivedContext rows keep a FIXED slot and a
// FIXED scale across every page, so flipping pages replays the scrubber's
// grow/shrink instead of reflowing.
//
// This module turns an ordered list of highlight snapshots into:
//   · a UNION axis order (first-appearance) for BFs (columns) + derivedContexts
//     (rows), so every surviving label sits in the same slot on every page;
//   · a per-page state for each axis entry — active | deprecated | hidden —
//     mirroring the live TimelineView diff (`diffSnapshots`): never-existed
//     entries are HIDDEN, retired ones show the deprecate UI at the transition;
//   · a single global cell/row scale sized to the full union, reused verbatim
//     on every page (no per-page zoom-to-fit).
//
// Kept free of React/DOM so it's unit-testable and shareable between the deck
// route and the export script. Axis visibility follows the live matrix: a BF is
// "served" when a present derivedContext lists it in `functions` (the same edge
// `buildDerivedContextFunctionGate` gates on), and a derivedContext is present when it
// carries at least one default-visible skill (matching PlatformMapMatrix).

import type { DerivedContext } from '@lib/api-types/derived-contexts';
import type { TimelineSnapshot } from '@lib/api-types/views';

import { isDefaultVisibleSkill } from './data-views.ts';

/** One deck page: a curated highlight snapshot + its narrative. */
export interface DeckPageInput {
  /** Normalized ISO date (YYYY-MM-DD) of the highlight. */
  date: string;
  /** Optional short heading (highlight title). */
  title?: string | null;
  /** Pop-scientist topline shown as the page's description. */
  summary: string;
  /** The composed platform-map snapshot at `date`. */
  snapshot: TimelineSnapshot;
}

/** Per-axis-entry state on a given page. */
export type AxisState = 'active' | 'deprecated' | 'hidden';

/** The single global scale, applied identically to every page. */
export interface DeckScale {
  /** Width of each BF (union) column, px. */
  cellPx: number;
  /** Height of each derivedContext (union) row, px. */
  rowPx: number;
  /** Column-header band height, px. */
  headerPx: number;
  /** DerivedContext-label column width, px (name + skill-count stacked in one cell). */
  compLabelPx: number;
  /** Grid gap between cells, px. */
  gapPx: number;
}

/** Resolved per-page axis visibility + diff, parallel to the input pages. */
export interface DeckPageAxes {
  /** State of every union BF id on this page. */
  bfState: Map<string, AxisState>;
  /** State of every union derivedContext id on this page. */
  compState: Map<string, AxisState>;
  /** Union-ordered BF ids that render this page (active | deprecated). */
  visibleBfIds: string[];
  /** Union-ordered derivedContext ids that render this page (active | deprecated). */
  visibleCompIds: string[];
  /** BF ids that became active on this page (vs. the prior page). */
  newBfIds: Set<string>;
  /** DerivedContext ids that became active on this page (vs. the prior page). */
  newCompIds: Set<string>;
}

export interface DeckLayout {
  /** Union BF ids in first-appearance order (columns, left→right). */
  bfOrder: string[];
  /** Union derivedContext ids in first-appearance order (rows, top→bottom). */
  compOrder: string[];
  /** id → display for every union BF (latest display wins). */
  bfDisplay: Map<string, string>;
  /** id → display for every union derivedContext (latest display wins). */
  compDisplay: Map<string, string>;
  /** id → default-visible skill count for every union derivedContext (latest wins). */
  compSkillCount: Map<string, number>;
  /** The one global scale, reused on every page. */
  scale: DeckScale;
  /** Per-page resolved axes, parallel to the input `pages`. */
  pages: DeckPageAxes[];
}

/** 4:3 content box the matrix must fit into, and scale clamps. */
export interface DeckDims {
  /** Inner width available to the matrix (main column), px. */
  contentW: number;
  /** Inner height available to the matrix (below the header), px. */
  contentH: number;
}

// Content box widened (narrower description aside) and the label band collapsed
// from three columns (name · connector · count) to one — the name renders in
// full with the skill count stacked beneath it.
export const DEFAULT_DIMS: DeckDims = { contentW: 694, contentH: 520 };

const CELL_MIN = 26;
const CELL_MAX = 72;
const ROW_MIN = 34;
const ROW_MAX = 56;
const HEADER_PX = 52; // two-line BF column headers
const COMP_LABEL_PX = 160; // one wide column: full name + count on line 2
const GAP_PX = 5;

/** Default-visible skills (frameworks / services / institutional; libraries hidden). */
export function visibleSkillCount(comp: DerivedContext): number {
  return comp.skills.reduce((n, s) => (isDefaultVisibleSkill(s) ? n + 1 : n), 0);
}

/** A derivedContext is "present" if it carries ≥1 default-visible skill (matches the live matrix). */
function presentComps(snapshot: TimelineSnapshot): DerivedContext[] {
  return snapshot.derivedContexts.filter((c) => visibleSkillCount(c) > 0);
}

/**
 * The active axes on a single snapshot:
 *  · a BF is active when some present derivedContext serves it (`comp.functions`)
 *    and it isn't declared-only (`state === 'planned'`);
 *  · a derivedContext is active when it's present AND serves ≥1 active BF
 *    (an "empty" derivedContext serving nothing active is hidden).
 */
export function activeAxes(snapshot: TimelineSnapshot): {
  activeBf: Set<string>;
  activeComp: Set<string>;
} {
  const comps = presentComps(snapshot);
  const plannedOnly = new Set(
    snapshot.business_functions.filter((f) => f.state === 'planned').map((f) => f.id),
  );
  // Only BFs that actually exist in the snapshot catalog are real columns.
  const realBf = new Set(snapshot.business_functions.map((f) => f.id));

  const activeBf = new Set<string>();
  for (const c of comps) {
    for (const fnId of c.functions) {
      if (realBf.has(fnId) && !plannedOnly.has(fnId)) activeBf.add(fnId);
    }
  }
  const activeComp = new Set<string>();
  for (const c of comps) {
    if (c.functions.some((fnId) => activeBf.has(fnId))) activeComp.add(c.id);
  }
  return { activeBf, activeComp };
}

function deriveState(
  id: string,
  activeNow: Set<string>,
  activePrior: Set<string>,
): AxisState {
  if (activeNow.has(id)) return 'active';
  if (activePrior.has(id)) return 'deprecated'; // retired at this transition
  return 'hidden';
}

/**
 * Build the full deck layout from an ordered (chronological) list of pages.
 * `pages` must be sorted ascending by date — first-appearance order derives
 * directly from that sequence.
 */
export function buildDeckLayout(
  pages: DeckPageInput[],
  dims: DeckDims = DEFAULT_DIMS,
): DeckLayout {
  const bfDisplay = new Map<string, string>();
  const compDisplay = new Map<string, string>();
  const compSkillCount = new Map<string, number>();
  const bfOrder: string[] = [];
  const compOrder: string[] = [];
  const seenBf = new Set<string>();
  const seenComp = new Set<string>();

  // Pair each page with its active axes so we never index a parallel array
  // (keeps strict noUncheckedIndexedAccess happy and the prior-page walk clean).
  const withActive = pages.map((page) => ({ page, active: activeAxes(page.snapshot) }));

  for (const { page, active } of withActive) {
    // Displays / skill counts: latest snapshot to name an entry wins (modern
    // naming-pass display), so read from the raw snapshot every page.
    for (const f of page.snapshot.business_functions) bfDisplay.set(f.id, f.display);
    for (const c of page.snapshot.derivedContexts) {
      compDisplay.set(c.id, c.display);
      compSkillCount.set(c.id, visibleSkillCount(c));
    }
    // First-appearance order: append an id the first page it becomes active.
    for (const id of active.activeBf) if (!seenBf.has(id)) { seenBf.add(id); bfOrder.push(id); }
    for (const id of active.activeComp) if (!seenComp.has(id)) { seenComp.add(id); compOrder.push(id); }
  }

  const scale = computeScale(bfOrder.length, compOrder.length, dims);

  const emptyActive = { activeBf: new Set<string>(), activeComp: new Set<string>() };
  let prior = emptyActive;
  const pageAxes: DeckPageAxes[] = withActive.map(({ active: now }) => {
    const bfState = new Map<string, AxisState>();
    const newBfIds = new Set<string>();
    for (const id of bfOrder) {
      const st = deriveState(id, now.activeBf, prior.activeBf);
      bfState.set(id, st);
      if (st === 'active' && !prior.activeBf.has(id)) newBfIds.add(id);
    }
    const compState = new Map<string, AxisState>();
    const newCompIds = new Set<string>();
    for (const id of compOrder) {
      const st = deriveState(id, now.activeComp, prior.activeComp);
      compState.set(id, st);
      if (st === 'active' && !prior.activeComp.has(id)) newCompIds.add(id);
    }
    const axes: DeckPageAxes = {
      bfState,
      compState,
      visibleBfIds: bfOrder.filter((id) => bfState.get(id) !== 'hidden'),
      visibleCompIds: compOrder.filter((id) => compState.get(id) !== 'hidden'),
      newBfIds,
      newCompIds,
    };
    prior = now; // advance the diff baseline to this page
    return axes;
  });

  return { bfOrder, compOrder, bfDisplay, compDisplay, compSkillCount, scale, pages: pageAxes };
}

/**
 * The single global scale: size cells/rows so the FULL union fits the content
 * box, clamped to sane bounds, then reuse identically on every page. Sizing to
 * the union (max extent) — not per page — is what keeps the scale constant, so
 * early pages read as a small map in the corner and later ones fill the frame.
 */
export function computeScale(
  bfCount: number,
  compCount: number,
  dims: DeckDims = DEFAULT_DIMS,
): DeckScale {
  const labelBand = COMP_LABEL_PX + GAP_PX;
  const colsW = dims.contentW - labelBand;
  const cellPx = clamp(Math.floor(colsW / Math.max(1, bfCount)) - GAP_PX, CELL_MIN, CELL_MAX);
  const rowsH = dims.contentH - HEADER_PX;
  const rowPx = clamp(Math.floor(rowsH / Math.max(1, compCount)) - GAP_PX, ROW_MIN, ROW_MAX);
  return {
    cellPx,
    rowPx,
    headerPx: HEADER_PX,
    compLabelPx: COMP_LABEL_PX,
    gapPx: GAP_PX,
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
